import React, { useEffect, useState } from "react";

import type { ClientId } from "../../domain/types";
import { ESCALATIONS_PREVIEW_KEY, toEscalationsPreview } from "../../domain/escalations";
import {
  addClientContact,
  fetchEscalationsState,
  moveClientContact,
  removeClientContact,
  type ClientContactDTO,
  type ContactListKind,
  type EscalationsStateDTO,
} from "../../data/escalationsApi";
import { persistAnswer, useCoClient } from "../../data/store";
import { CoButton } from "../../components/Button";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, SearchIcon, TrashIcon, XIcon } from "../../components/icons";
import { useSubStepNav } from "../useSubStepNav";


function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground",
        className
      )}
    >
      {initials(name)}
    </span>
  );
}

/** Someone the picker can offer — a firm user or a Books org user. */
interface PickerPerson {
  key: string;
  name: string;
  email: string;
  /** Set on the client's own Zoho Books people — tagged so they don't read as firm staff. */
  books?: boolean;
  add: () => void;
}

function ContactIdentity({ contact }: { contact: ClientContactDTO }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="truncate text-sm font-medium">{contact.name}</span>
      <span className="truncate text-xs text-muted-foreground">
        {contact.email}
        {contact.phone ? ` · ${contact.phone}` : ""}
      </span>
    </div>
  );
}

/**
 * The attach flow, in the team screen's Attach-users shape: a search over
 * everyone still addable — the firm directory plus the client's own Zoho
 * Books people, the latter tagged. Directory-only on purpose; the manual
 * name/email form this once had is gone.
 */
function AddContactPicker({
  people,
  busy,
  booksFailed,
}: {
  people: PickerPerson[];
  busy: boolean;
  booksFailed: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  if (!open) {
    return (
      <CoButton variant="outline" className="w-full" onClick={() => setOpen(true)}>
        <PlusIcon className="size-3.5" /> Attach contacts
      </CoButton>
    );
  }

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? people.filter(
        (person) => person.name.toLowerCase().includes(needle) || person.email.toLowerCase().includes(needle)
      )
    : people;

  function closeAndReset() {
    setOpen(false);
    setQuery("");
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Attach contacts</span>
        <button
          type="button"
          aria-label="Close attach contacts"
          onClick={closeAndReset}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/40"
        >
          <XIcon className="size-3.5" />
        </button>
      </div>

      <div className="flex items-center gap-2 rounded-md border bg-background px-2.5">
        <SearchIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search your firm and the client's Zoho Books"
          className="h-9 w-full bg-transparent text-sm outline-none"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground">No one matches.</p>
      ) : (
        <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
          {filtered.map((person) => (
            <li key={person.key}>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  person.add();
                  closeAndReset();
                }}
                className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-muted/40 disabled:opacity-50"
              >
                <Avatar name={person.name} className="size-7" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">{person.name}</span>
                  <span className="truncate text-xs text-muted-foreground">{person.email}</span>
                </div>
                {person.books && (
                  <span className="shrink-0 rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    Zoho Books
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {booksFailed && (
        <p className="text-xs text-muted-foreground">
          Couldn't read the client's Zoho Books people just now - the firm list still works.
        </p>
      )}
    </div>
  );
}

/**
 * Escalations & access: two contact lists picked from a deliberately broad
 * directory — the whole firm plus the client's own Zoho Books users. The
 * escalation list is ORDERED — that order is pushed to the tenant at go-live
 * and shown on its escalations page, which is display, not notification
 * routing: nothing on the tenant sends anything from it yet. Dashboard access
 * is plain membership.
 *
 * The server owns every rule (dedupe per list, email shape, position
 * bookkeeping) and answers each mutation with the whole screen state — this
 * component sends the change and renders what comes back, leaving the
 * `escalationsAccess` answer behind for the workspace preview.
 */
export function EscalationsAccessStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "escalations-access");

  const [state, setState] = useState<EscalationsStateDTO | undefined>();
  /** The load itself failing — different thing from a refused mutation. */
  const [refusal, setRefusal] = useState<string | null>(null);
  /** A refused mutation's sentence — "already on this list…". */
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);

  function absorb(next: EscalationsStateDTO) {
    setState(next);
    void persistAnswer(clientId, ESCALATIONS_PREVIEW_KEY, toEscalationsPreview(next));
  }

  useEffect(() => {
    let live = true;

    setRefusal(null);
    fetchEscalationsState(clientId)
      .then((next) => {
        if (!live) return;
        setState(next);
        void persistAnswer(clientId, ESCALATIONS_PREVIEW_KEY, toEscalationsPreview(next));
      })
      .catch((error: unknown) => {
        if (live) setRefusal(error instanceof Error ? error.message : "Could not reach the server.");
      });

    return () => {
      live = false;
    };
  }, [clientId, attempt]);

  if (!client) return null;

  async function apply(call: () => Promise<EscalationsStateDTO>) {
    setBusy(true);
    try {
      absorb(await call());
      setActionError(null);
    } catch (error: unknown) {
      setActionError(error instanceof Error ? error.message : "That change did not go through.");
    } finally {
      setBusy(false);
    }
  }

  const escalation = state?.escalation ?? [];
  const dashboard = state?.dashboard ?? [];
  const ready = state !== undefined && escalation.length > 0;

  /** Everyone a list can still add — both directories minus that list's members. */
  function candidatesFor(list: ContactListKind, rows: ClientContactDTO[]): PickerPerson[] {
    const taken = new Set(rows.map((row) => row.email.toLowerCase()));
    const firm = (state?.firm ?? [])
      .filter((candidate) => !taken.has(candidate.email.toLowerCase()))
      .map((candidate) => ({
        key: `firm-${candidate.user_id}`,
        name: candidate.name,
        email: candidate.email,
        add: () => void apply(() => addClientContact(clientId, list, { user_id: candidate.user_id })),
      }));
    const books = (state?.books.people ?? [])
      .filter((person) => !taken.has(person.email.toLowerCase()))
      .map((person) => ({
        key: `books-${person.email}`,
        name: person.name,
        email: person.email,
        books: true,
        add: () =>
          void apply(() =>
            addClientContact(clientId, list, {
              name: person.name,
              email: person.email,
              phone: person.phone ?? undefined,
              source: "books",
            })
          ),
      }));
    return [...firm, ...books];
  }

  function picker(list: ContactListKind, rows: ClientContactDTO[]) {
    return (
      <AddContactPicker
        people={candidatesFor(list, rows)}
        busy={busy}
        booksFailed={state?.books.failed ?? false}
      />
    );
  }

  return (
    <SubStepShell
      title="Set Up Client Contacts"
      description="Set up the client contacts Zournal should use for escalations and select who should have access to the Client Dashboard."
      thenWhat={ready ? "Next: Month-close checklist" : "Continue opens once someone is on the escalation list."}
      onContinue={() => void goNext()}
      continueDisabled={!ready}
    >
      {state === undefined && refusal === null ? (
        <div className="rounded-lg border border-dashed px-5 py-4 text-[13px] text-muted-foreground">
          Reading the contact lists…
        </div>
      ) : refusal !== null ? (
        <div className="flex flex-col items-start gap-2 rounded-lg border border-dashed px-5 py-4">
          <p className="text-sm text-muted-foreground">{refusal}</p>
          <CoButton variant="outline" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </CoButton>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <section className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h3 className="text-base font-medium">Escalation contacts</h3>
              <p className="text-sm text-muted-foreground">
                Add the people on the client side who should be contacted if we need information or if their team is
                not responding. This could be the CFO, CEO, business owner, or any other appropriate contact.
              </p>
            </div>

            {escalation.length === 0 ? (
              <p className="text-sm text-muted-foreground">No escalation contacts yet.</p>
            ) : (
              <ol className="flex flex-col gap-2">
                {escalation.map((contact, index) => (
                  <li
                    key={contact.contact_id}
                    className="flex items-center justify-between gap-2 rounded-md border p-3 text-sm"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
                        {index + 1}
                      </span>
                      <ContactIdentity contact={contact} />
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        aria-label={`Move ${contact.name} up`}
                        disabled={busy || index === 0}
                        onClick={() => void apply(() => moveClientContact(clientId, contact.contact_id, "up"))}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/40 disabled:opacity-40"
                      >
                        <ArrowUpIcon className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${contact.name} down`}
                        disabled={busy || index === escalation.length - 1}
                        onClick={() => void apply(() => moveClientContact(clientId, contact.contact_id, "down"))}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/40 disabled:opacity-40"
                      >
                        <ArrowDownIcon className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Remove ${contact.name} from escalation contacts`}
                        disabled={busy}
                        onClick={() => void apply(() => removeClientContact(clientId, contact.contact_id))}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted/40 disabled:opacity-40"
                      >
                        <TrashIcon className="size-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {picker("escalation", escalation)}
          </section>

          <section className="flex flex-col gap-4 border-t pt-8">
            <div className="flex flex-col gap-1">
              <h3 className="text-base font-medium">Client Dashboard Access</h3>
              <p className="text-sm text-muted-foreground">
                Add the people who should have access to the Client Dashboard. They can log in at any time to view
                the latest financial numbers, reports, and closed months.
              </p>
              <p className="text-sm text-muted-foreground">
                These users will receive an email notification whenever a month is closed and the latest reports are
                available.
              </p>
            </div>

            {dashboard.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recipients yet.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {dashboard.map((contact) => (
                  <li
                    key={contact.contact_id}
                    className="flex items-center justify-between gap-2 rounded-md border p-3 text-sm"
                  >
                    <ContactIdentity contact={contact} />
                    <button
                      type="button"
                      aria-label={`Remove ${contact.name}`}
                      disabled={busy}
                      onClick={() => void apply(() => removeClientContact(clientId, contact.contact_id))}
                      className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted/40 disabled:opacity-40"
                    >
                      <TrashIcon className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {picker("dashboard", dashboard)}
          </section>

          {actionError && <p className="text-sm text-destructive">{actionError}</p>}
          {state !== undefined && escalation.length === 0 && (
            <p className="text-sm text-destructive">
              Add at least one escalation contact - someone must be told when this client's work needs attention.
            </p>
          )}
        </div>
      )}
    </SubStepShell>
  );
}
